// OIGG Service for handling OIG API interactions
import {
  affablebpmIntegrationService,
  AFFABLE_LICENSE_SKU_NAME,
} from "./affablebpmIntegration";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

// Base URL for OIG API
const BASE_URL = getApiOrigin();
// API Endpoints
const BEARER_TOKEN_ENDPOINT = "/api/v1/oig/get-bearer-token";
const ADVERSE_CHECK_ENDPOINT = "/api/v1/oig/get-adverse-check";
const ADVERSE_CHECK_HISTORY_ENDPOINT = "/api/v1/oig/adverse-check-history";
const ADVERSE_CHECK_HISTORY_SUMMARY_ENDPOINT =
  "/api/v1/oig/adverse-check-history/summary";
const ADVERSE_CHECK_HISTORY_DOWNLOAD_ENDPOINT =
  "/api/v1/oig/adverse-check-history/download";
const BULK_ADVERSE_CHECK_ASYNC_ENDPOINT =
  "/api/v1/oig/bulk-adverse-check/async";
const BULK_ADVERSE_CHECK_JOB_ENDPOINT =
  "/api/v1/oig/bulk-adverse-check-jobs";
const WORKFLOW_MAPPING_ENDPOINT = "/api/v1/workflow-mapping/by-type-and-org";
const PROCESS_EXCLUSION_DATA_PATH = "/Records/UniComply_ProcessExclusionData/";

// Session storage keys for caching AffableBPM base URLs
const AFFABLE_BASEURL_CACHE_PREFIX = "affable_bpm_baseurl_";
const AFFABLE_ORGABBREV_CACHE_PREFIX = "affable_bpm_orgabbrev_";

// Helper functions for caching
interface CachedAffableData {
  baseUrl: string;
  orgabbrev: string;
}

function getCachedAffableDataFromSession(
  facilityKey: string
): CachedAffableData | null {
  if (typeof window === "undefined") return null;
  try {
    const baseUrlKey = `${AFFABLE_BASEURL_CACHE_PREFIX}${facilityKey}`;
    const orgabbrevKey = `${AFFABLE_ORGABBREV_CACHE_PREFIX}${facilityKey}`;
    const cachedBaseUrl = sessionStorage.getItem(baseUrlKey);
    const cachedOrgabbrev = sessionStorage.getItem(orgabbrevKey);

    if (
      cachedBaseUrl &&
      cachedOrgabbrev &&
      cachedBaseUrl.trim() !== "" &&
      cachedOrgabbrev.trim() !== ""
    ) {
      return {
        baseUrl: cachedBaseUrl.trim(),
        orgabbrev: cachedOrgabbrev.trim(),
      };
    }
    return null;
  } catch (error) {
    console.error(
      "Error reading cached Affable data from sessionStorage:",
      error
    );
    return null;
  }
}

function setCachedAffableDataToSession(
  facilityKey: string,
  baseUrl: string,
  orgabbrev: string
): void {
  if (typeof window === "undefined") return;
  try {
    const baseUrlKey = `${AFFABLE_BASEURL_CACHE_PREFIX}${facilityKey}`;
    const orgabbrevKey = `${AFFABLE_ORGABBREV_CACHE_PREFIX}${facilityKey}`;
    sessionStorage.setItem(baseUrlKey, baseUrl);
    sessionStorage.setItem(orgabbrevKey, orgabbrev);
  } catch (error) {
    console.error("Error saving Affable data to sessionStorage:", error);
  }
}

// Interfaces for OIG API responses
export interface OIGBearerTokenResponse {
  success: boolean;
  data: {
    access_token: string;
    expires_in: number;
    token_type: string;
  };
  error: string | null;
  message: string;
  timestamp: string;
}

export interface OIGBearerTokenData {
  access_token: string;
  expires_in: number;
  token_type: string;
}

// Interfaces for Adverse Check API
export interface AdverseCheckRequest {
  bearer_token: string;
  name: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  ssn?: string;
  phoneNumber?: string;
  licenses?: Array<{
    number: string;
  }>;
  facilityId?: number;
}

export type OigEntitlementSnapshot = {
  allowed: boolean;
  remaining: number;
  limit: number;
  used: number;
  included_checks: number;
  purchased_addon_count: number;
  period: string;
  low_checks_threshold_reached: boolean;
};

export class OigEntitlementError extends Error {
  code: string;
  remaining?: number;
  limit?: number;
  used?: number;
  period?: string;
  admin_email?: string;
  recommended_target?: number;
  recommended_blocks?: Array<{ plan_id?: number; checks?: number; price_usd?: number }>;
  estimated_checks?: number;

  constructor(detail: {
    code?: string;
    message?: string;
    remaining?: number;
    limit?: number;
    used?: number;
    period?: string;
    admin_email?: string;
    recommended_target?: number;
    recommended_blocks?: Array<{ plan_id?: number; checks?: number; price_usd?: number }>;
    estimated_checks?: number;
  }) {
    super(detail.message || "Your monthly exclusion-check allowance is exhausted.");
    this.name = "OigEntitlementError";
    this.code = detail.code || "insufficient_oig_checks";
    this.remaining = detail.remaining;
    this.limit = detail.limit;
    this.used = detail.used;
    this.period = detail.period;
    this.admin_email = detail.admin_email;
    this.recommended_target = detail.recommended_target;
    this.recommended_blocks = detail.recommended_blocks;
    this.estimated_checks = detail.estimated_checks;
  }
}

export function isOigEntitlementError(error: unknown): error is OigEntitlementError {
  if (error instanceof OigEntitlementError) {
    return true;
  }
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: string }).code === "insufficient_oig_checks"
  );
}

function entitlementErrorFromPayload(parsed: unknown): OigEntitlementError | null {
  const detail = (parsed as { detail?: unknown } | null)?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    ("code" in detail) &&
    ((detail as { code?: string }).code === "insufficient_oig_checks" ||
      (detail as { code?: string }).code === "oig_checks_not_enabled")
  ) {
    return new OigEntitlementError(detail as ConstructorParameters<typeof OigEntitlementError>[0]);
  }
  return null;
}

export interface SsnVerification {
  status: "matched" | "not_matched" | "skipped" | "unavailable" | string;
  reason: string;
  verifiedNames?: Array<{
    first?: string | null;
    middle?: string | null;
    last?: string | null;
    fullName?: string | null;
    matched?: boolean;
  }>;
  checkedCount?: number;
  source?: string;
}

export interface AdverseCheckName {
  first: string;
  middle: string | null;
  last: string;
  fullName: string | null;
  prefix: string | null;
  suffix: string | null;
  businessName: string | null;
}

export interface AdverseCheckAddress {
  address: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

export interface AdverseCheckExclusion {
  name: AdverseCheckName;
  address: AdverseCheckAddress;
  dateOfBirth: string | null;
  licenseNumber: string;
  profession: string;
  additionalComments: string | null;
  excludedDate: string;
  excludingAgency: string | null;
  exclusionsType: string | null;
  listCode: string;
  reinstatementDate: string | null;
  amendment: string | null;
  npi: string[];
  EXCLUSION_ORIGIN?: string;
  _DATA?: Record<string, unknown>;
}

export interface AdverseCheckProfessional {
  propelusId: string;
  score: number;
  npi: string;
  names: AdverseCheckName[];
  phones: Array<{
    number: string;
    type: string;
  }>;
  attributes: {
    exclusions: AdverseCheckExclusion[];
  };
}

// API returns data directly on item, not wrapped in "professional"
export interface AdverseCheckItem {
  propelusId?: string;
  score?: number;
  npi?: string;
  names?: AdverseCheckName[];
  phones?: Array<{
    number: string;
    type: string;
  }>;
  attributes?: {
    exclusions: AdverseCheckExclusion[];
  };
}

export interface AdverseCheckData {
  code: string;
  message: string;
  customProperties: Record<string, any>;
  items: AdverseCheckItem[];
  total_exclusions: number;
  summary: string;
  ssnVerification?: SsnVerification | null;
}

export interface AdverseCheckResponse {
  success: boolean;
  data: AdverseCheckData;
  error: string | null;
  message: string;
  timestamp: string;
}

export interface AdverseCheckHistorySubject {
  name: string;
  npi?: string;
  phone?: string;
  licenses?: Array<{ number: string }>;
  ssn?: string;
  response?: Record<string, unknown> | AdverseCheckData | null;
}

export interface AdverseCheckHistoryCreateRequest {
  facility_id: number;
  org_id?: number | null;
  queried_at: string;
  tz_abbrev: string;
  subjects: AdverseCheckHistorySubject[];
  export_rows: Array<Record<string, unknown>>;
}

export interface AdverseCheckHistoryCreateResponse {
  success: boolean;
  data?: {
    batch_id: number;
    facility_id: number;
    subject_count: number;
  } | null;
  error?: string | null;
  message?: string;
}

export interface AdverseCheckHistorySummaryResponse {
  success: boolean;
  data?: {
    facility_id: number;
    batch_count: number;
  } | null;
  error?: string | null;
  message?: string;
}

export interface AdverseCheckHistoryListItem {
  batch_id: number;
  run_name: string;
  run_type: string;
  queried_at: string;
  record_count: number;
  operator: string;
}

export interface AdverseCheckHistoryListResponse {
  success: boolean;
  data?: {
    facility_id: number;
    total: number;
    page: number;
    page_size: number;
    items: AdverseCheckHistoryListItem[];
  } | null;
  error?: string | null;
  message?: string;
}

export interface BulkAdverseCheckSubjectRequest {
  name: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  ssn?: string;
  phoneNumber?: string;
  licenses?: Array<{ number: string }>;
  checkType?: "Vendor" | "Employee";
}

export interface BulkAdverseCheckStartRequest {
  facilityId: number;
  orgId?: number | null;
  queriedAt: string;
  tzAbbrev: string;
  subjects: BulkAdverseCheckSubjectRequest[];
}

export interface BulkAdverseCheckStartResponse {
  job_id: number;
  status: "pending" | "running" | "completed" | "failed";
  poll_url: string;
}

export interface BulkAdverseCheckResult {
  success: boolean;
  data: AdverseCheckData | null;
  error?: { message?: string } | null;
  message?: string;
  subject: Omit<BulkAdverseCheckSubjectRequest, "ssn">;
}

export interface BulkAdverseCheckJobResponse {
  job_id: number;
  status: "pending" | "running" | "completed" | "failed";
  completed_count: number;
  total_count: number;
  error_message?: string | null;
  response?: {
    results: BulkAdverseCheckResult[];
    total_count: number;
    completed_count: number;
  } | null;
}

// Interfaces for Workflow Mapping API
export interface WorkflowMappingField {
  DataType: string;
  FieldName: string;
  Col_Parent: number;
  Uni_FieldName: string;
}

export interface WorkflowMappingData {
  id: number;
  workflowId: number;
  facilityId: number;
  field_Mapping: WorkflowMappingField[];
  orgid: number;
  record_added_flag: boolean;
}

export interface WorkflowMappingResponse {
  success: boolean;
  count: number;
  data: WorkflowMappingData[];
}

// Token storage keys
const OIG_TOKEN_KEY = "oig_access_token";
const OIG_TOKEN_EXPIRES_KEY = "oig_token_expires_at";

class OIGGService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = BASE_URL;
  }

  /**
   * Extract a meaningful error message from a failed fetch Response
   */
  private async extractErrorMessage(response: Response): Promise<string> {
    try {
      const text = await response.text();
      if (!text) return response.statusText;
      try {
        const json = JSON.parse(text);
        const msg = json?.detail || json?.message || json?.error || json;
        return typeof msg === "string" ? msg : JSON.stringify(msg);
      } catch (_) {
        return text;
      }
    } catch (_) {
      return response.statusText;
    }
  }

  /**
   * Get bearer token from OIG API
   */
  async getBearerToken(): Promise<OIGBearerTokenData> {
    try {
      const response = await authenticatedFetch(`${this.baseUrl}${BEARER_TOKEN_ENDPOINT}`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const msg = await this.extractErrorMessage(response);
        throw new Error(`HTTP ${response.status}: ${msg}`);
      }

      const data: OIGBearerTokenResponse = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to retrieve bearer token");
      }

      // Store token in localStorage
      this.storeToken(data.data);

      return data.data;
    } catch (error) {
      console.error("Error fetching OIG bearer token:", error);
      throw error;
    }
  }

  /**
   * Get valid bearer token (from cache or fetch new one)
   */
  async getValidBearerToken(): Promise<string> {
    const cachedToken = this.getCachedToken();

    if (cachedToken && !this.isTokenExpired()) {
      return cachedToken;
    }

    // Token is expired or doesn't exist, fetch new one
    const tokenData = await this.getBearerToken();
    return tokenData.access_token;
  }

  /**
   * Store token in localStorage
   */
  private storeToken(tokenData: OIGBearerTokenData): void {
    localStorage.setItem(OIG_TOKEN_KEY, tokenData.access_token);
    localStorage.setItem(
      OIG_TOKEN_EXPIRES_KEY,
      String(Date.now() + tokenData.expires_in * 1000)
    );
  }

  /**
   * Get cached token from localStorage
   */
  private getCachedToken(): string | null {
    return localStorage.getItem(OIG_TOKEN_KEY);
  }

  /**
   * Check if token is expired
   */
  private isTokenExpired(): boolean {
    const expStr = localStorage.getItem(OIG_TOKEN_EXPIRES_KEY);
    if (!expStr) return true;

    const exp = Number(expStr);
    return Number.isFinite(exp) ? Date.now() >= exp : true;
  }

  /**
   * Clear stored token
   */
  clearToken(): void {
    localStorage.removeItem(OIG_TOKEN_KEY);
    localStorage.removeItem(OIG_TOKEN_EXPIRES_KEY);
  }

  /**
   * Check if user has a valid OIG token
   */
  isAuthenticated(): boolean {
    const token = this.getCachedToken();
    return token !== null && !this.isTokenExpired();
  }

  /**
   * Make authenticated request to OIG API
   */
  async makeAuthenticatedRequest(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const token = await this.getValidBearerToken();

    const defaultHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    return fetch(`${this.baseUrl}${endpoint}`, requestOptions);
  }

  /**
   * Get adverse check for a professional
   */
  async getAdverseCheck(
    params: Omit<AdverseCheckRequest, "bearer_token">
  ): Promise<AdverseCheckData> {
    try {
      // Fetch a fresh token each time to match the working manual payload behavior
      const fresh = await this.getBearerToken();
      const bearerToken = fresh.access_token;

      const requestPayload: AdverseCheckRequest = {
        ...params,
        bearer_token: bearerToken,
      };

      const response = await authenticatedFetch(`${this.baseUrl}${ADVERSE_CHECK_ENDPOINT}`, {
        method: "POST",
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const text = await response.text();
        let parsed: any = null;
        try {
          parsed = text ? JSON.parse(text) : null;
        } catch {
          parsed = null;
        }
        const entitlementError = entitlementErrorFromPayload(parsed);
        if (entitlementError) {
          throw entitlementError;
        }
        const msg =
          parsed?.error?.details?.message ||
          parsed?.error?.message ||
          parsed?.message ||
          text ||
          `HTTP ${response.status}`;
        const error: any = new Error(msg);
        if (parsed?.error?.details) {
          error.details = parsed.error.details;
        }
        throw error;
      }

      const data: AdverseCheckResponse = await response.json();

      if (!data.success) {
        const apiError: any = data?.error || {};
        const msg =
          apiError?.details?.message ||
          apiError?.message ||
          data?.message ||
          "Adverse check failed";
        const err: any = new Error(msg);
        if (apiError?.details) {
          err.details = apiError.details;
        }
        throw err;
      }

      return data.data;
    } catch (error) {
      console.error("Error performing adverse check:", error);
      throw error;
    }
  }

  /**
   * Get adverse check with simplified parameters
   */
  async performAdverseCheck(
    name: string,
    npi: string,
    ssn: string,
    phoneNumber: string,
    licenseNumbers: string[],
    facilityId: number,
    nameParts?: { firstName?: string; lastName?: string }
  ): Promise<AdverseCheckData> {
    const payload: Omit<AdverseCheckRequest, "bearer_token"> = {
      name,
      facilityId,
    };
    const firstName = (nameParts?.firstName || "").trim();
    const lastName = (nameParts?.lastName || "").trim();
    if (firstName) {
      payload.firstName = firstName;
    }
    if (lastName) {
      payload.lastName = lastName;
    }
    const trimmedNpi = npi.trim();
    if (trimmedNpi) {
      payload.npi = trimmedNpi;
    }
    // SSN is never sent to Propelus; Rules uses it only for rare LEIE Verify on uncertain matches.
    const normalizedSsn = ssn.replace(/\D/g, "");
    if (normalizedSsn.length === 9) {
      payload.ssn = normalizedSsn;
    }
    const trimmedPhone = phoneNumber.trim();
    if (trimmedPhone) {
      payload.phoneNumber = trimmedPhone;
    }
    const cleanedLicenses = licenseNumbers
      .map((number) => number.trim())
      .filter((number) => number.length > 0)
      .map((number) => ({ number }));
    if (cleanedLicenses.length > 0) {
      payload.licenses = cleanedLicenses;
    }
    return this.getAdverseCheck(payload);
  }

  /** UI status suffix from optional LEIE SSN verification. */
  formatSsnVerificationLabel(
    data: AdverseCheckData | null | undefined
  ): string | null {
    const status = data?.ssnVerification?.status;
    if (!status || status === "skipped") {
      return null;
    }
    if (status === "matched") {
      return "SSN verified";
    }
    if (status === "not_matched") {
      return "No SSN match on LEIE";
    }
    if (status === "unavailable") {
      return "LEIE verify unavailable";
    }
    return null;
  }

  /**
   * Check if professional has any exclusions
   */
  hasExclusions(adverseCheckData: AdverseCheckData): boolean {
    return adverseCheckData.total_exclusions > 0;
  }

  /**
   * Get all exclusions from adverse check data
   */
  getAllExclusions(
    adverseCheckData: AdverseCheckData
  ): AdverseCheckExclusion[] {
    const exclusions: AdverseCheckExclusion[] = [];

    adverseCheckData.items.forEach((item) => {
      // API returns attributes directly on item, not wrapped in "professional"
      if (item.attributes?.exclusions) {
        exclusions.push(...item.attributes.exclusions);
      }
    });

    return exclusions;
  }

  /**
   * Get exclusions by profession
   */
  getExclusionsByProfession(
    adverseCheckData: AdverseCheckData,
    profession: string
  ): AdverseCheckExclusion[] {
    return this.getAllExclusions(adverseCheckData).filter((exclusion) =>
      exclusion.profession.toLowerCase().includes(profession.toLowerCase())
    );
  }

  /**
   * Get exclusions by date range
   */
  getExclusionsByDateRange(
    adverseCheckData: AdverseCheckData,
    startDate: string,
    endDate: string
  ): AdverseCheckExclusion[] {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return this.getAllExclusions(adverseCheckData).filter((exclusion) => {
      const exclusionDate = new Date(exclusion.excludedDate);
      return exclusionDate >= start && exclusionDate <= end;
    });
  }

  /**
   * Get summary of adverse check results
   */
  getAdverseCheckSummary(adverseCheckData: AdverseCheckData): {
    hasExclusions: boolean;
    totalExclusions: number;
    professions: string[];
    dateRange: { earliest: string | null; latest: string | null };
  } {
    const exclusions = this.getAllExclusions(adverseCheckData);
    const professions = [...new Set(exclusions.map((e) => e.profession))];

    const dates = exclusions
      .map((e) => e.excludedDate)
      .filter((date) => date && date !== "0019-08-27") // Filter out invalid dates
      .sort();

    return {
      hasExclusions: exclusions.length > 0,
      totalExclusions: exclusions.length,
      professions,
      dateRange: {
        earliest: dates.length > 0 ? dates[0] : null,
        latest: dates.length > 0 ? dates[dates.length - 1] : null,
      },
    };
  }

  /**
   * Generic method to handle OIG API responses
   */
  async handleOIGResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP ${response.status}: ${errorText || response.statusText}`
      );
    }

    const data = await response.json();

    if (data.success === false) {
      throw new Error(data.message || "OIG API request failed");
    }

    return data;
  }

  /**
   * Get workflow mapping by workflow ID
   */
  async getWorkflowMapping(
    type: string,
    org_name: string
  ): Promise<WorkflowMappingData[]> {
    try {
      const response = await authenticatedFetch(
        `${this.baseUrl}${WORKFLOW_MAPPING_ENDPOINT}?type=${encodeURIComponent(
          type
        )}&org_name=${encodeURIComponent(org_name)}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const msg = await this.extractErrorMessage(response);
        throw new Error(`HTTP ${response.status}: ${msg}`);
      }

      const data: WorkflowMappingResponse = await response.json();

      if (!data.success) {
        throw new Error("Failed to retrieve workflow mapping");
      }

      return data.data;
    } catch (error) {
      console.error("Error fetching workflow mapping:", error);
      throw error;
    }
  }

  /**
   * Process exclusion data by sending to UniComply
   */
  async processExclusionData(processData: any, orgName: string): Promise<any> {
    try {
      const normalizedOrgName = orgName?.trim();
      if (!normalizedOrgName) {
        throw new Error("orgName is required to process exclusion data.");
      }

      // Try to get baseUrl from session storage first
      let affableData = getCachedAffableDataFromSession(normalizedOrgName);

      // If not cached, fetch from API
      if (!affableData) {
        console.log("Base URL not found in cache, fetching from API...");
        const licenseResponse =
          await affablebpmIntegrationService.getLicenseAndFeaturesForOrg({
            skuName: AFFABLE_LICENSE_SKU_NAME,
            orgName: normalizedOrgName,
          });

        const baseUrl = licenseResponse?.response?.org_info?.base_url;
        const orgabbrev = licenseResponse?.response?.org_info?.orgabbrev;

        if (
          !baseUrl ||
          (typeof baseUrl === "string" && baseUrl.trim() === "")
        ) {
          throw new Error("Base URL not found. Please contact support.");
        }

        if (
          !orgabbrev ||
          (typeof orgabbrev === "string" && orgabbrev.trim() === "")
        ) {
          throw new Error("Organization not found. Please contact support.");
        }

        affableData = {
          baseUrl: baseUrl.trim(),
          orgabbrev: orgabbrev.trim(),
        };

        // Cache the fetched data in session storage
        setCachedAffableDataToSession(
          normalizedOrgName,
          affableData.baseUrl,
          affableData.orgabbrev
        );
        console.log("Base URL cached successfully");
      } else {
        console.log("Using cached Base URL from session storage");
      }

      // Build the endpoint URL
      const normalizedBaseUrl = affableData.baseUrl.replace(/\/+$/, "");
      const endpoint = `${normalizedBaseUrl}${PROCESS_EXCLUSION_DATA_PATH}`;

      console.log("Processing exclusion data with endpoint:", endpoint);
      console.log("Processing data being sent:", processData);

      // Create FormData object for multipart/form-data
      const formData = new FormData();
      formData.append('strObjInput', JSON.stringify(processData));

      console.log("Processing data being sent:", processData);

      

      const response = await fetch(endpoint, {
        method: "POST",
        // Don't set Content-Type header - let the browser set it with boundary
        body: formData,
      });

      if (!response.ok) {
        const msg = await this.extractErrorMessage(response);
        throw new Error(`HTTP ${response.status}: ${msg}`);
      }

      const data = await response.json();

      const statusRaw =
        data?.Status ?? data?.status ?? data?.STATUS;
      const statusStr =
        typeof statusRaw === "string" ? statusRaw.trim().toLowerCase() : "";
      const isFailed =
        statusStr === "failed" ||
        data?.Success === false ||
        data?.success === false;

      if (isFailed) {
        const msg =
          (typeof data?.ErrorMsg === "string" && data.ErrorMsg.trim()) ||
          (typeof data?.errorMsg === "string" && data.errorMsg.trim()) ||
          (typeof data?.message === "string" && data.message.trim()) ||
          (typeof data?.Message === "string" && data.Message.trim()) ||
          "AffableFLOW could not process exclusion data.";
        throw new Error(msg);
      }

      return data;
    } catch (error) {
      console.error("Error processing exclusion data:", error);
      throw error;
    }
  }

  /**
   * Persist one adverse-check history batch (single or bulk run).
   */
  async saveAdverseCheckHistory(
    body: AdverseCheckHistoryCreateRequest
  ): Promise<AdverseCheckHistoryCreateResponse> {
    const response = await authenticatedFetch(
      `${this.baseUrl}${ADVERSE_CHECK_HISTORY_ENDPOINT}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    return (await response.json()) as AdverseCheckHistoryCreateResponse;
  }

  async startBulkAdverseCheck(
    body: BulkAdverseCheckStartRequest,
  ): Promise<BulkAdverseCheckStartResponse> {
    const response = await authenticatedFetch(
      `${this.baseUrl}${BULK_ADVERSE_CHECK_ASYNC_ENDPOINT}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const text = await response.text();
      let parsed: unknown = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const entitlementError = entitlementErrorFromPayload(parsed);
      if (entitlementError) {
        throw entitlementError;
      }
      const msg =
        typeof parsed === "object" && parsed && "detail" in parsed
          ? JSON.stringify((parsed as { detail: unknown }).detail)
          : text || response.statusText;
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    return (await response.json()) as BulkAdverseCheckStartResponse;
  }

  async getOigEntitlement(facilityId: number): Promise<OigEntitlementSnapshot> {
    const params = new URLSearchParams({ facility_id: String(facilityId) });
    const response = await authenticatedFetch(
      `${this.baseUrl}/api/v1/oig/entitlement?${params.toString()}`,
      { method: "GET" },
    );
    if (!response.ok) {
      const text = await response.text();
      let parsed: unknown = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const entitlementError = entitlementErrorFromPayload(parsed);
      if (entitlementError) {
        throw entitlementError;
      }
      throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
    }
    return (await response.json()) as OigEntitlementSnapshot;
  }

  async getBulkAdverseCheckJob(
    jobId: number,
  ): Promise<BulkAdverseCheckJobResponse> {
    const response = await authenticatedFetch(
      `${this.baseUrl}${BULK_ADVERSE_CHECK_JOB_ENDPOINT}/${jobId}`,
      { method: "GET" },
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    return (await response.json()) as BulkAdverseCheckJobResponse;
  }

  /**
   * Get stored history batch count for a facility (UI enablement).
   */
  async getAdverseCheckHistorySummary(
    facilityId: number
  ): Promise<AdverseCheckHistorySummaryResponse> {
    const params = new URLSearchParams({
      facility_id: String(facilityId),
    });
    const response = await authenticatedFetch(
      `${this.baseUrl}${ADVERSE_CHECK_HISTORY_SUMMARY_ENDPOINT}?${params.toString()}`,
      { method: "GET" }
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    return (await response.json()) as AdverseCheckHistorySummaryResponse;
  }

  /**
   * Download full-lifetime adverse-check history Excel for a facility.
   */
  async downloadAdverseCheckHistory(
    facilityId: number,
    tzAbbrev: string
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams({
      facility_id: String(facilityId),
      tz_abbrev: tzAbbrev,
    });
    const response = await authenticatedFetch(
      `${this.baseUrl}${ADVERSE_CHECK_HISTORY_DOWNLOAD_ENDPOINT}?${params.toString()}`,
      { method: "GET" }
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename =
      match?.[1] ||
      `Adverse_Check_${new Date().toISOString().slice(0, 16)}.xlsx`;
    const blob = await response.blob();
    return { blob, filename };
  }

  /**
   * Paginated history runs for a facility (History tab / #10121).
   */
  async listAdverseCheckHistory(
    facilityId: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<AdverseCheckHistoryListResponse> {
    const params = new URLSearchParams({
      facility_id: String(facilityId),
      page: String(page),
      page_size: String(pageSize),
    });
    const response = await authenticatedFetch(
      `${this.baseUrl}${ADVERSE_CHECK_HISTORY_ENDPOINT}?${params.toString()}`,
      { method: "GET" }
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    return (await response.json()) as AdverseCheckHistoryListResponse;
  }

  /**
   * Download Excel audit copy for one persisted history run (#10111).
   */
  async downloadAdverseCheckHistoryRun(
    batchId: number
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await authenticatedFetch(
      `${this.baseUrl}${ADVERSE_CHECK_HISTORY_ENDPOINT}/${batchId}/download`,
      { method: "GET" }
    );
    if (!response.ok) {
      const msg = await this.extractErrorMessage(response);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename =
      match?.[1] || `exclusion_check_run_${batchId}.xlsx`;
    const blob = await response.blob();
    return { blob, filename };
  }
}

// Export singleton instance
export const oiggService = new OIGGService();

// Export the class for testing purposes
export default OIGGService;
